// app/frontend/components/ui/Drawers/FilterDrawer.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Stack,
  Box,
  Group,
  Checkbox,
  Divider,
  Button,
} from "@mantine/core";
import { L2KH5, L2KH6, L2KText, L2KCaption, L2KBadge } from "../Typography";
import type { Video, Category, LearningTrack } from "../../../types/video";

export type FilterState = {
  selectedCategory: number | null;
  selectedTrack: number | null;
  selectedCategories: number[];
  selectedSkillLevels: string[];
  selectedTags: string[];
  selectedTracks: number[];
};

type Props = {
  opened: boolean;
  onClose: () => void;

  // Source data (for counts + preview calculation)
  videos: Video[];
  learningTracks: LearningTrack[];
  categories: Category[];

  // Current filters from page (seed the draft with these on open)
  currentFilters: FilterState;

  // Emit finalized filters when user clicks "Show X videos"
  onApply: (filters: FilterState) => void;
};

const emptyFilters: FilterState = {
  selectedCategory: null,
  selectedTrack: null,
  selectedCategories: [],
  selectedSkillLevels: [],
  selectedTags: [],
  selectedTracks: [],
};

export default function FilterDrawer({
  opened,
  onClose,
  videos,
  learningTracks,
  categories,
  currentFilters,
  onApply,
}: Props) {
  // Local draft (not applied until "Show X videos")
  const [draft, setDraft] = useState<FilterState>(
    currentFilters ?? emptyFilters
  );

  // When drawer opens, reset draft to currentFilters
  useEffect(() => {
    if (opened) setDraft(currentFilters ?? emptyFilters);
  }, [opened, currentFilters]);

  // Skill levels from videos
  const skillLevels = useMemo(
    () =>
      Array.from(new Set(videos.map((v) => v.skill_level)))
        .filter(Boolean)
        .sort(),
    [videos]
  );

  // Group skill levels by prefix
  const groupedSkillLevels = useMemo(
    () => ({
      Beginner: skillLevels.filter((lvl) =>
        lvl.toLowerCase().includes("beginner")
      ),
      Improver: skillLevels.filter((lvl) =>
        lvl.toLowerCase().includes("improver")
      ),
      Intermediate: skillLevels.filter((lvl) =>
        lvl.toLowerCase().includes("intermediate")
      ),
      Advanced: skillLevels.filter((lvl) =>
        lvl.toLowerCase().includes("advanced")
      ),
    }),
    [skillLevels]
  );

  // If skill levels are simple strings like "beginner", "improver", etc.
  const simpleSkillLevels = useMemo(() => {
    const simple = ["beginner", "improver", "intermediate", "advanced"];
    return skillLevels.filter((lvl) => simple.includes(lvl.toLowerCase()));
  }, [skillLevels]);

  const allTags = useMemo(
    () => Array.from(new Set(videos.flatMap((v) => v.tags || []))).sort(),
    [videos]
  );

  // Helpers: mutate draft
  const setSelectedCategory = (categoryId: number | null) =>
    setDraft((s) => ({
      ...s,
      selectedCategory: categoryId,
      selectedCategories: categoryId ? [categoryId] : [],
    }));

  const setSelectedTrack = (trackId: number | null) =>
    setDraft((s) => ({
      ...s,
      selectedTrack: trackId,
      selectedTracks: trackId ? [trackId] : [],
    }));

  const toggleSkillLevel = (level: string) =>
    setDraft((s) => {
      const exists = s.selectedSkillLevels.includes(level);
      return {
        ...s,
        selectedSkillLevels: exists
          ? s.selectedSkillLevels.filter((x) => x !== level)
          : [...s.selectedSkillLevels, level],
      };
    });

  const toggleTag = (tag: string) =>
    setDraft((s) => {
      const exists = s.selectedTags.includes(tag);
      return {
        ...s,
        selectedTags: exists
          ? s.selectedTags.filter((x) => x !== tag)
          : [...s.selectedTags, tag],
      };
    });

  // const clearAll = () => setDraft(emptyFilters);

  // Preview count based on draft
  const previewVideosCount = useMemo(() => {
    let result = videos;

    if (draft.selectedTracks.length > 0) {
      const ids = draft.selectedTracks.flatMap((trackId) => {
        const t = learningTracks.find((x) => x.id === trackId);
        return t ? t.video_ids : [];
      });
      result = result.filter((v) => ids.includes(v.id));
    }

    if (draft.selectedCategories.length > 0) {
      result = result.filter((v) =>
        v.categories.some((cat) => draft.selectedCategories.includes(cat.id))
      );
    }

    if (draft.selectedSkillLevels.length > 0) {
      result = result.filter((v) =>
        draft.selectedSkillLevels.includes(v.skill_level)
      );
    }

    if (draft.selectedTags.length > 0) {
      result = result.filter((v) =>
        draft.selectedTags.some((t) => (v.tags || []).includes(t))
      );
    }

    return result.length;
  }, [videos, learningTracks, draft]);

  // Active filter count
  const activeFilterCount =
    draft.selectedCategories.length +
    draft.selectedSkillLevels.length +
    draft.selectedTags.length +
    draft.selectedTracks.length;

  // Apply / Cancel
  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleCancel = () => {
    // Discard draft and close
    setDraft(currentFilters ?? emptyFilters);
    onClose();
  };

  // Helper to count videos per category
  const getCategoryVideoCount = (categoryId: number) => {
    return videos.filter((v) =>
      v.categories.some((cat) => cat.id === categoryId)
    ).length;
  };

  // Format skill level for display
  const formatSkillLevel = (level: string) => {
    return level.charAt(0).toUpperCase() + level.slice(1);
  };

  // Check if we have simple skill levels (beginner, improver, etc.) or complex ones
  const hasSimpleSkillLevels = simpleSkillLevels.length > 0;

  return (
    <Drawer
      opened={opened}
      onClose={handleCancel}
      title={<L2KH5>Advanced Filters</L2KH5>}
      position="right"
      size="md"
      withCloseButton
      closeOnClickOutside={false}
      styles={{
        content: {
          background: "rgba(23, 23, 23, 0.95)",
          border: "1px solid rgba(255, 152, 0, 0.3)",
          backdropFilter: "blur(15px)",
          display: "flex",
          flexDirection: "column",
        },
        header: {
          backgroundColor: "transparent",
          borderBottom: "1px solid rgba(255, 152, 0, 0.3)",
        },
        title: { color: "#F0F0F0" },
        body: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          paddingBottom: 0,
          overflow: "hidden",
        },
      }}
    >
      {/* Sticky summary under header */}
      <Box
        style={{
          padding: "12px 12px 8px 12px",
          borderBottom: "1px solid rgba(255, 152, 0, 0.2)",
        }}
      >
        <Group justify="space-between" wrap="wrap">
          <L2KH6 mb={0}>Filters</L2KH6>
          {activeFilterCount > 0 && (
            <L2KBadge variant="accent" radius="xl">
              {activeFilterCount}
            </L2KBadge>
          )}
        </Group>
        <L2KCaption variant="tertiary" style={{ fontSize: "10px" }}>
          Choose a category or learning track, and refine with levels & tags
        </L2KCaption>
      </Box>

      {/* Scrollable middle */}
      <Box
        style={{ flex: 1, overflow: "auto", padding: 12, paddingBottom: 16 }}
      >
        <Stack gap="lg">
          {/* Categories */}
          <Box>
            <L2KH5 mb="xs">Categories</L2KH5>
            <L2KCaption variant="tertiary" mb="md">
              Choose one category
            </L2KCaption>
            <Stack gap="xs">
              {categories.map((category) => {
                const count = getCategoryVideoCount(category.id);
                const checked = draft.selectedCategories.includes(category.id);
                return (
                  <Checkbox
                    key={category.id}
                    label={
                      <Group justify="space-between" style={{ width: "100%" }}>
                        <L2KText variant="secondary" size="xs">
                          {category.name}
                        </L2KText>
                        <L2KBadge size="xs" variant="muted">
                          {count}
                        </L2KBadge>
                      </Group>
                    }
                    checked={checked}
                    onChange={() =>
                      setSelectedCategory(checked ? null : category.id)
                    }
                    color="orange"
                    styles={{
                      label: { color: "#D0D0D0", width: "100%" },
                      input: {
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        borderColor: "rgba(255, 152, 0, 0.3)",
                        "&:checked": {
                          backgroundColor: "#FF9800",
                          borderColor: "#FF9800",
                        },
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>

          <Divider color="rgba(255, 152, 0, 0.3)" />

          {/* Learning Tracks */}
          {learningTracks.length > 0 && (
            <>
              <Box>
                <L2KH5 mb="xs">Learning Tracks</L2KH5>
                <L2KCaption variant="tertiary" mb="md">
                  Choose one learning path
                </L2KCaption>
                <Stack gap="xs">
                  {learningTracks.map((track) => {
                    const checked = draft.selectedTracks.includes(track.id);
                    return (
                      <Checkbox
                        key={track.id}
                        label={
                          <Box>
                            <L2KText variant="secondary" fw={500} size="xs">
                              {track.name}
                            </L2KText>
                            <L2KCaption
                              variant="tertiary"
                              style={{ fontSize: "10px" }}
                            >
                              {track.estimated_duration} •{" "}
                              {track.difficulty_range}
                            </L2KCaption>
                          </Box>
                        }
                        checked={checked}
                        onChange={() =>
                          setSelectedTrack(checked ? null : track.id)
                        }
                        color="orange"
                        styles={{
                          label: { color: "#D0D0D0" },
                          input: {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderColor: "rgba(255, 152, 0, 0.3)",
                            "&:checked": {
                              backgroundColor: "#FF9800",
                              borderColor: "#FF9800",
                            },
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
              <Divider color="rgba(255, 152, 0, 0.3)" />
            </>
          )}

          {/* Skill Levels */}
          <Box>
            <L2KH5 mb="xs">Skill Levels</L2KH5>
            <L2KCaption variant="tertiary" mb="md" style={{ fontSize: "10px" }}>
              Select multiple levels.
            </L2KCaption>
            {hasSimpleSkillLevels ? (
              <Stack gap="xs">
                {simpleSkillLevels.map((level) => {
                  const count = videos.filter(
                    (v) => v.skill_level === level
                  ).length;
                  return (
                    <Checkbox
                      key={level}
                      label={
                        <Group
                          justify="space-between"
                          style={{ width: "100%" }}
                        >
                          <L2KText variant="secondary" size="xs">
                            {formatSkillLevel(level)}
                          </L2KText>
                          <L2KBadge size="xs" variant="muted">
                            {count}
                          </L2KBadge>
                        </Group>
                      }
                      checked={draft.selectedSkillLevels.includes(level)}
                      onChange={() => toggleSkillLevel(level)}
                      color="orange"
                      styles={{
                        label: { color: "#D0D0D0", width: "100%" },
                        input: {
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          borderColor: "rgba(255, 152, 0, 0.3)",
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            ) : (
              <Stack gap="sm">
                {Object.entries(groupedSkillLevels).map(
                  ([group, lvls]) =>
                    lvls.length > 0 && (
                      <Box key={group}>
                        <L2KText size="sm" c="#FF9800" mb="xs" fw={600}>
                          {group}
                        </L2KText>
                        <Stack gap="xs" ml="md">
                          {lvls.map((level) => (
                            <Checkbox
                              key={level}
                              label={
                                <L2KText variant="secondary" size="xs">
                                  {level}
                                </L2KText>
                              }
                              checked={draft.selectedSkillLevels.includes(
                                level
                              )}
                              onChange={() => toggleSkillLevel(level)}
                              color="orange"
                              styles={{
                                label: { color: "#D0D0D0" },
                                input: {
                                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                                  borderColor: "rgba(255, 152, 0, 0.3)",
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )
                )}
              </Stack>
            )}
          </Box>

          {/* Tags */}
          {allTags.length > 0 && (
            <>
              <Divider color="rgba(255, 152, 0, 0.3)" />
              <Box>
                <L2KH5 mb="xs">Tags</L2KH5>
                <L2KCaption
                  variant="tertiary"
                  mb="md"
                  style={{ fontSize: "10px" }}
                >
                  Select multiple tags.
                </L2KCaption>
                <Stack gap="xs">
                  {allTags.map((tag) => {
                    const count = videos.filter((v) =>
                      (v.tags || []).includes(tag)
                    ).length;
                    const checked = draft.selectedTags.includes(tag);
                    return (
                      <Checkbox
                        key={tag}
                        label={
                          <Group
                            justify="space-between"
                            style={{ width: "100%" }}
                          >
                            <L2KText variant="secondary" size="xs">
                              {tag}
                            </L2KText>
                            <L2KBadge size="xs" variant="muted">
                              {count}
                            </L2KBadge>
                          </Group>
                        }
                        checked={checked}
                        onChange={() => toggleTag(tag)}
                        color="orange"
                        styles={{
                          label: { color: "#D0D0D0", width: "100%" },
                          input: {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderColor: "rgba(255, 152, 0, 0.3)",
                            "&:checked": {
                              backgroundColor: "#FF9800",
                              borderColor: "#FF9800",
                            },
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </Box>

      {/* Fixed footer */}
      <Box
        style={{
          borderTop: "1px solid rgba(255, 152, 0, 0.2)",
          padding: 12,
          background: "rgba(23, 23, 23, 0.85)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Group gap="8px" grow>
          <Button
            variant="default"
            size="sm"
            onClick={handleCancel}
            style={{ backgroundColor: "rgba(255,255,255,0.06)" }}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            size="sm"
            onClick={handleApply}
            style={{ backgroundColor: "#FF9800", color: "#171717" }}
          >
            Show {previewVideosCount}{" "}
            {previewVideosCount === 1 ? "Video" : "Videos"}
          </Button>
        </Group>
      </Box>
    </Drawer>
  );
}
